import { useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface DeleteMyDataDialogProps {
  trigger?: React.ReactNode;
  className?: string;
}

const LOCAL_STORAGE_PREFIXES = [
  "iso27001_project_",
  "iso27001_tool_",
  "rm_",
];

const LOCAL_STORAGE_KEYS = [
  "iso27001kit_user_email",
  "copilot_conversations",
  "copilot_active_id",
];

const USER_DATA_TABLES = [
  "audit_room_evidence",
  "audit_room_checklist_items",
  "audit_rooms",
  "control_risk_links",
  "asset_process_links",
  "risk_treatments",
  "risk_register",
  "controls",
  "risk_assets",
  "business_processes",
  "threat_library",
  "vulnerability_library",
  "security_incidents",
  "risk_frameworks",
  "audit_log",
] as const;

export function DeleteMyDataDialog({ trigger, className }: DeleteMyDataDialogProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const clearLocalData = () => {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (LOCAL_STORAGE_KEYS.includes(key)) {
          keysToRemove.push(key);
          continue;
        }
        if (LOCAL_STORAGE_PREFIXES.some((p) => key.startsWith(p))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch (error) {
      console.error("Failed to clear local data", error);
    }
  };

  const deleteServerData = async () => {
    if (!user) return;
    for (const table of USER_DATA_TABLES) {
      const { error } = await supabase.from(table as any).delete().eq("user_id", user.id);
      if (error) {
        console.error(`Failed to delete from ${table}:`, error);
      }
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      clearLocalData();
      if (user) {
        await deleteServerData();
      }
      toast.success("Your data has been deleted");
      setOpen(false);
      // Reload so any in-memory caches reset
      setTimeout(() => window.location.reload(), 600);
    } catch (error) {
      console.error("Delete data failed", error);
      toast.error("Something went wrong while deleting your data");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            className={
              className ??
              "text-sm text-white/60 hover:text-accent transition-colors inline-flex items-center gap-1"
            }
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete my data
          </button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete all your data?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently remove every saved project, tool draft, risk register entry,
            audit room, asset, control and AI chat history tied to this browser
            {user ? " and your account" : ""}. Your account login{user ? "" : " (if any)"} stays intact -
            you can delete the account itself from the Account page.
            <br /><br />
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleDelete();
            }}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete my data
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
